import { API } from '@/App';

export const LABEL_FORMAT_OPTIONS = [
  {
    code: 'A',
    name: 'Mini',
    title: 'A — Mini 57 mm',
    description: '57 mm, vertical',
    sizeLabel: '57 mm',
  },
  {
    code: 'B',
    name: 'Standard',
    title: 'B — 50 × 50',
    description: '50 × 50 mm, carré',
    sizeLabel: '50 × 50',
  },
  {
    code: 'C',
    name: 'Pro',
    title: 'C — Pro 100 × 75',
    description: '100 × 75 mm, horizontal',
    sizeLabel: '100 × 75',
  },
];

export const DEFAULT_LABEL_FORMAT = 'A';

const LABEL_ELIGIBLE_STATUSES = new Set([
  'paid',
  'awaiting_assignment',
  'pickup_in_progress',
  'picked_up',
  'in_transit',
  'at_relay',
  'transferred',
  'out_for_delivery',
  'delivered',
  'delivered_with_proof',
  'client_absent',
  'delivery_rescheduled',
  'delivery_failed',
]);

export const normalizeLabelFormat = (rawValue) => {
  const normalized = String(rawValue || '').trim().toUpperCase();
  return LABEL_FORMAT_OPTIONS.some((option) => option.code === normalized)
    ? normalized
    : DEFAULT_LABEL_FORMAT;
};

export const getLabelFormatOption = (formatCode) =>
  LABEL_FORMAT_OPTIONS.find((option) => option.code === normalizeLabelFormat(formatCode)) || LABEL_FORMAT_OPTIONS[0];

export const getDeliveryLabelCapabilities = (delivery) => {
  const capabilities = delivery?.label_capabilities;
  if (capabilities && typeof capabilities.available === 'boolean') {
    return {
      available: capabilities.available,
      reason: capabilities.reason || '',
      supportedFormats: Array.isArray(capabilities.supported_formats) && capabilities.supported_formats.length > 0
        ? capabilities.supported_formats.map(normalizeLabelFormat)
        : LABEL_FORMAT_OPTIONS.map((option) => option.code),
    };
  }

  const trackingCode = String(delivery?.tracking_code || '').trim();
  const status = String(delivery?.status || '').trim().toLowerCase();

  if (!trackingCode && status === 'pending_payment') {
    return {
      available: false,
      reason: "L'étiquette sera disponible après validation du paiement et génération du code de suivi.",
      supportedFormats: LABEL_FORMAT_OPTIONS.map((option) => option.code),
    };
  }

  if (!trackingCode) {
    return {
      available: false,
      reason: "Le code de suivi n'est pas encore disponible pour cette livraison.",
      supportedFormats: LABEL_FORMAT_OPTIONS.map((option) => option.code),
    };
  }

  if (!LABEL_ELIGIBLE_STATUSES.has(status)) {
    return {
      available: false,
      reason: "L'étiquette n'est pas disponible pour ce statut de livraison.",
      supportedFormats: LABEL_FORMAT_OPTIONS.map((option) => option.code),
    };
  }

  return {
    available: true,
    reason: '',
    supportedFormats: LABEL_FORMAT_OPTIONS.map((option) => option.code),
  };
};

export const buildDeliveryLabelPreviewEndpoint = (deliveryId, formatCode) =>
  `${API}/deliveries/${deliveryId}/label?format=${normalizeLabelFormat(formatCode)}`;

export const buildDeliveryLabelDownloadEndpoint = (deliveryId, formatCode) =>
  `${API}/deliveries/${deliveryId}/label/download?format=${normalizeLabelFormat(formatCode)}`;

export const resolveDeliveryLabelFilename = ({ headers, delivery, formatCode }) => {
  const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    return decodeURIComponent(utfMatch[1]);
  }

  const basicMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (basicMatch?.[1]) {
    return basicMatch[1];
  }

  const trackingCode = String(delivery?.tracking_code || '').trim() || `delivery-${String(delivery?.id || '').slice(0, 8)}`;
  return `label-${trackingCode}-${normalizeLabelFormat(formatCode)}.pdf`;
};

export const blobFromAxiosResponse = (data) => (
  data instanceof Blob
    ? data
    : new Blob([data], { type: 'application/pdf' })
);

export const readBlobErrorMessage = async (error, fallbackMessage) => {
  const directDetail = error?.response?.data?.detail || error?.response?.data?.message;
  if (typeof directDetail === 'string' && directDetail.trim()) {
    return directDetail.trim();
  }

  const blob = error?.response?.data;
  if (blob instanceof Blob) {
    try {
      const text = await blob.text();
      if (!text) {
        return fallbackMessage;
      }
      try {
        const parsed = JSON.parse(text);
        return parsed?.detail || parsed?.message || fallbackMessage;
      } catch (_parseError) {
        return text.trim() || fallbackMessage;
      }
    } catch (_blobError) {
      return fallbackMessage;
    }
  }

  return fallbackMessage;
};

export const downloadBlobFile = ({ blob, filename }) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 0);
};

export const printPdfBlobUrl = (blobUrl) => new Promise((resolve, reject) => {
  try {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Impression étiquette');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';

    const cleanup = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const fallbackTimer = window.setTimeout(() => {
      cleanup();
      resolve();
    }, 2000);

    iframe.onload = () => {
      window.clearTimeout(fallbackTimer);
      try {
        iframe.contentWindow?.focus?.();
        iframe.contentWindow?.print?.();
        resolve();
      } catch (error) {
        reject(error);
      } finally {
        window.setTimeout(cleanup, 1200);
      }
    };

    iframe.onerror = () => {
      window.clearTimeout(fallbackTimer);
      cleanup();
      reject(new Error('PRINT_LOAD_FAILED'));
    };

    document.body.appendChild(iframe);
    iframe.src = blobUrl;
  } catch (error) {
    reject(error);
  }
});
